import { useEffect, useState, useRef } from "react";
import { api, API, authImageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import MemberCombobox from "@/components/MemberCombobox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, FileSpreadsheet, KeyRound, Image as ImageIcon, BarChart2, RefreshCw, Settings2, CheckCircle2, Clock, Crown, Network, Download, BookOpen, Video, Search } from "lucide-react";
import { Laurel } from "@/components/LaurelBadge";

const EMPTY_MEMBER = { member_number: "", carica: "", cognome: "", nome: "", club: "", divisione: "", telefono: "", email: "", indirizzo: "", join_date: "", onorario: false };
const EMPTY_EVENT = { title: "", description: "", image_url: "", video_url: "", link: "", date: "", end_date: "", category: "Distretto", club: "", divisione: "" };
const NOTIFY_LABEL = "giorgioteresio.palma@gmail.com";

export default function Admin() {
  const [members, setMembers] = useState([]);
  const [events, setEvents] = useState([]);
  const [breakdown, setBreakdown] = useState({ total: 0, by_club: [], by_division: [] });
  const [syncStatus, setSyncStatus] = useState(null);
  const [orgConfig, setOrgConfig] = useState({ officers: [], lieutenants: {} });
  const [divisions, setDivisions] = useState([]);
  const [memberDialog, setMemberDialog] = useState(null);
  const [eventDialog, setEventDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [privacyLog, setPrivacyLog] = useState([]);
  const [privacyVersion, setPrivacyVersion] = useState(null);
  const [pdfCover, setPdfCover] = useState({ cover_image_url: "", social_year: "" });
  const [resetRequests, setResetRequests] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const excelRef = useRef();
  const settingsExcelRef = useRef();
  const photoRefs = useRef({});
  const eventImgRef = useRef();
  const eventVideoRef = useRef();
  const [videoProgress, setVideoProgress] = useState(null);
  const coverImgRef = useRef();

  const loadMembers = () => api.get("/members").then((r) => setMembers(r.data));
  const loadEvents = () => api.get("/events").then((r) => setEvents(r.data));
  const loadBreakdown = () => api.get("/admin/breakdown").then((r) => setBreakdown(r.data));
  const loadSyncStatus = () => api.get("/admin/sync-status").then((r) => setSyncStatus(r.data));
  const loadOrg = () => api.get("/admin/org-config").then((r) => setOrgConfig({ officers: r.data.officers || [], lieutenants: r.data.lieutenants || {} }));
  const loadDivisions = () => api.get("/filters").then((r) => {
    const divSort = (n) => { const m = String(n).match(/^\s*(\d+)/); return m ? parseInt(m[1], 10) : 9999; };
    setDivisions([...(r.data.divisions || [])].sort((a, b) => divSort(a) - divSort(b) || a.localeCompare(b)));
  });
  const loadPrivacy = () => api.get("/admin/privacy-registry").then((r) => setPrivacyLog(r.data));
  const loadPrivacyVersion = () => api.get("/admin/privacy-version").then((r) => setPrivacyVersion(r.data));
  const loadPdfCover = () => api.get("/admin/pdf-cover").then((r) => setPdfCover({ cover_image_url: r.data.cover_image_url || "", social_year: r.data.social_year || "" }));
  const loadResetRequests = () => api.get("/admin/reset-requests").then((r) => setResetRequests(r.data));

  const savePdfCover = async () => {
    setBusy(true);
    try {
      await api.put("/admin/pdf-cover", pdfCover);
      toast.success("Impostazioni copertina PDF salvate");
    } catch (e) { toast.error("Errore nel salvataggio della copertina"); }
    finally { setBusy(false); }
  };

  const uploadCoverImg = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/pdf-cover/upload", fd);
      setPdfCover((c) => ({ ...c, cover_image_url: data.image_url }));
      toast.success("Immagine di copertina caricata. Ricorda di salvare.");
    } catch (e) { toast.error("Errore caricamento immagine"); }
  };

  const publishPrivacyVersion = async () => {
    if (!window.confirm("Pubblicare una nuova versione dell'informativa? Tutti i soci dovranno accettarla nuovamente al prossimo accesso.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/privacy-version/publish");
      toast.success(`Nuova versione pubblicata (v${data.version}). Tutti i soci dovranno riaccettare.`);
      loadPrivacyVersion();
    } catch (e) { toast.error("Errore nella pubblicazione della nuova versione"); }
    finally { setBusy(false); }
  };

  const exportPrivacyCsv = () => {
    if (!privacyLog.length) { toast.error("Nessuna accettazione da esportare"); return; }
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["Nome", "Email", "Data e ora accettazione", "Versione accettata", "Non mostrare piu", "N. accettazioni"];
    const rows = privacyLog.map((p) => [
      p.name || "",
      p.email || "",
      p.privacy_accepted_at ? new Date(p.privacy_accepted_at).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" }) : "",
      `v${p.privacy_version_accepted ?? 1}`,
      p.privacy_hide ? "Si" : "No",
      p.privacy_accept_count ?? 1,
    ].map(esc).join(","));
    const csv = "\uFEFF" + [header.map(esc).join(","), ...rows].join("\r\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `registro-privacy-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => { loadMembers(); loadEvents(); loadBreakdown(); loadSyncStatus(); loadOrg(); loadDivisions(); loadPrivacy(); loadPrivacyVersion(); loadPdfCover(); loadResetRequests(); }, []);

  const saveOrg = async () => {
    setBusy(true);
    try {
      await api.put("/admin/org-config", orgConfig);
      toast.success("Organigramma salvato");
    } catch (e) { toast.error("Errore nel salvataggio dell'organigramma"); }
    finally { setBusy(false); }
  };
  const addOfficer = () => setOrgConfig((c) => ({ ...c, officers: [...c.officers, { role: "", member_id: "" }] }));
  const updOfficer = (i, field, val) => setOrgConfig((c) => { const o = [...c.officers]; o[i] = { ...o[i], [field]: val }; return { ...c, officers: o }; });
  const rmOfficer = (i) => setOrgConfig((c) => ({ ...c, officers: c.officers.filter((_, j) => j !== i) }));
  const setLieut = (div, val) => setOrgConfig((c) => ({ ...c, lieutenants: { ...c.lieutenants, [div]: val } }));

  // ---- Members ----
  const saveMember = async () => {
    setBusy(true);
    try {
      if (memberDialog.id) await api.put(`/members/${memberDialog.id}`, memberDialog);
      else await api.post("/members", memberDialog);
      toast.success("Socio salvato");
      setMemberDialog(null);
      loadMembers();
      loadBreakdown();
    } catch (e) { toast.error("Errore nel salvataggio"); }
    finally { setBusy(false); }
  };

  const deleteMember = async (id) => {
    if (!window.confirm("Eliminare questo socio?")) return;
    await api.delete(`/members/${id}`);
    toast.success("Socio eliminato");
    loadMembers();
    loadBreakdown();
  };

  const genResetLink = async (m) => {
    try {
      const { data } = await api.post(`/admin/members/${m.id}/reset-link`);
      try { await navigator.clipboard.writeText(data.reset_link); } catch (_) {}
      toast.success(`Link reset copiato — ${data.name || m.cognome}`, { description: data.reset_link, duration: 12000 });
      loadResetRequests();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nella generazione del link"); }
  };


  const uploadPhoto = async (id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/members/${id}/photo`, fd);
      toast.success("Foto caricata");
      loadMembers();
    } catch (e) { toast.error("Errore caricamento foto"); }
  };

  const importExcel = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/import-excel", fd);
      toast.success(`Import completato: ${data.inserted} nuovi, ${data.updated} aggiornati${data.removed ? `, ${data.removed} rimossi` : ""}`);
      if (data.errors?.length) toast.warning(`${data.errors.length} righe con problemi`);
      loadMembers();
      loadBreakdown();
      loadSyncStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore import Excel"); }
    finally { setBusy(false); if (excelRef.current) excelRef.current.value = ""; if (settingsExcelRef.current) settingsExcelRef.current.value = ""; }
  };

  const downloadBlob = (blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "credenziali_soci.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const generateCredentials = async (mode = "new") => {
    const heavy = mode === "all" || mode === "unchanged";
    const msg = mode === "all"
      ? "RIGENERARE una NUOVA password per TUTTI i soci con email? Le password precedenti (comprese quelle già personalizzate) non saranno più valide. Al termine scaricherai il CSV completo da distribuire."
      : mode === "unchanged"
      ? "Impostare la password universale #kidsneedkiwanis SOLO ai soci che non l'hanno ancora cambiata? Chi ha già scelto una password personale non verrà toccato. Al termine scaricherai il CSV."
      : "Generare credenziali per i soci con email che non hanno ancora un account?";
    if (!window.confirm(msg)) return;
    setBusy(true);
    const token = localStorage.getItem("kiwanis_token");
    try {
      if (heavy) {
        const start = await fetch(`${API}/admin/generate-credentials?mode=${mode}`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
        if (!start.ok) throw new Error();
        toast.info("Elaborazione in corso… può richiedere 1-3 minuti. Attendi il download.");
        let count = 0;
        let done = false;
        for (let i = 0; i < 120 && !done; i++) {
          await new Promise((r) => setTimeout(r, 3000));
          try {
            const s = await api.get("/admin/generate-credentials/status");
            if (s.data.status === "done") { count = s.data.count; done = true; }
          } catch (_) { /* ignora errori temporanei di rete durante il polling */ }
        }
        if (!done) {
          toast.info("L'operazione è ancora in corso sul server. Attendi un minuto e premi di nuovo il pulsante: se è terminata scaricherai subito il CSV.");
          return;
        }
        const dl = await fetch(`${API}/admin/generate-credentials/download`, { headers: { Authorization: `Bearer ${token}` } });
        if (!dl.ok) throw new Error("download");
        downloadBlob(await dl.blob());
        toast.success(`${count} password impostate. CSV scaricato. Ora questi soci accedono con #kidsneedkiwanis.`);
      } else {
        const res = await fetch(`${API}/admin/generate-credentials?mode=new`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error();
        const count = parseInt(res.headers.get("X-Created-Count") || "0", 10);
        const blob = await res.blob();
        if (count === 0) {
          toast.info("Tutti i soci con email hanno già un account. Usa 'Rigenera tutte le password' per un nuovo CSV.");
          return;
        }
        downloadBlob(blob);
        toast.success(`${count} credenziali generate. CSV scaricato. Notifica (senza password) inviata a ${NOTIFY_LABEL}.`);
      }
    } catch (e) { toast.error("Errore generazione credenziali. Riprova."); }
    finally { setBusy(false); }
  };

  // ---- Events ----
  const saveEvent = async () => {
    setBusy(true);
    try {
      if (eventDialog.id) await api.put(`/events/${eventDialog.id}`, eventDialog);
      else await api.post("/events", eventDialog);
      toast.success("Evento salvato");
      setEventDialog(null);
      loadEvents();
    } catch (e) { toast.error("Errore nel salvataggio"); }
    finally { setBusy(false); }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Eliminare questo evento?")) return;
    await api.delete(`/events/${id}`);
    toast.success("Evento eliminato");
    loadEvents();
  };

  const uploadEventImg = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/events/upload-image", fd);
      setEventDialog((d) => ({ ...d, image_url: `${process.env.REACT_APP_BACKEND_URL}${data.image_url}?auth=${localStorage.getItem("kiwanis_token")}`, video_url: "" }));
      toast.success("Immagine caricata");
    } catch (e) { toast.error("Errore caricamento immagine"); }
  };

  const uploadEventVideo = async (file) => {
    if (!file) return;
    if (file.size > 250 * 1024 * 1024) { toast.error("Video troppo grande (max 250 MB)"); return; }
    const CHUNK = 5 * 1024 * 1024;
    const total = Math.ceil(file.size / CHUNK);
    try {
      setVideoProgress(0);
      const { data: initData } = await api.post("/events/upload-video/init");
      const uploadId = initData.upload_id;
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("index", i);
        fd.append("chunk", blob, "chunk");
        await api.post("/events/upload-video/chunk", fd);
        setVideoProgress(Math.round(((i + 1) / total) * 100));
      }
      const { data } = await api.post("/events/upload-video/complete", { upload_id: uploadId, filename: file.name });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.video_url}?auth=${localStorage.getItem("kiwanis_token")}`;
      setEventDialog((d) => ({ ...d, video_url: url, image_url: "" }));
      toast.success("Video caricato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento video");
    } finally {
      setVideoProgress(null);
      if (eventVideoRef.current) eventVideoRef.current.value = "";
    }
  };

  const _mq = memberSearch.trim().toLowerCase();
  const filteredMembers = _mq
    ? members.filter((m) => {
        const full = `${m.nome || ""} ${m.cognome || ""}`.toLowerCase();
        const rev = `${m.cognome || ""} ${m.nome || ""}`.toLowerCase();
        return full.includes(_mq) || rev.includes(_mq) || (m.email || "").toLowerCase().includes(_mq);
      })
    : members;

  return (
    <div className="space-y-6" data-testid="admin-panel">
      <div>
        <p className="text-xs tracking-[0.15em] uppercase font-bold text-gold">Area di gestione</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-navy mt-2 tracking-tight">Pannello Admin</h1>
      </div>

      <Tabs defaultValue="soci">
        <TabsList className="bg-white border border-navy/10">
          <TabsTrigger value="soci" data-testid="tab-soci">Soci</TabsTrigger>
          <TabsTrigger value="eventi" data-testid="tab-eventi">Eventi & News</TabsTrigger>
          <TabsTrigger value="statistiche" data-testid="tab-statistiche">Statistiche</TabsTrigger>
          <TabsTrigger value="organigramma" data-testid="tab-organigramma">Organigramma</TabsTrigger>
          <TabsTrigger value="credenziali" data-testid="tab-credenziali">Credenziali</TabsTrigger>
          <TabsTrigger value="privacy" data-testid="tab-privacy">Privacy</TabsTrigger>
          <TabsTrigger value="reset" data-testid="tab-reset">Richieste reset</TabsTrigger>
          <TabsTrigger value="impostazioni" data-testid="tab-impostazioni">Impostazioni</TabsTrigger>
        </TabsList>

        {/* SOCI */}
        <TabsContent value="soci" className="mt-6 space-y-4">
          <div className="flex flex-wrap gap-3 items-center">
            <Button onClick={() => setMemberDialog({ ...EMPTY_MEMBER })} data-testid="add-member-btn" className="bg-navy hover:bg-navy-hover text-white">
              <Plus className="w-4 h-4 mr-1" /> Nuovo socio
            </Button>
            <input ref={excelRef} type="file" accept=".xlsx,.xls" hidden onChange={(e) => importExcel(e.target.files[0])} data-testid="excel-input" />
            <Button variant="outline" onClick={() => excelRef.current?.click()} disabled={busy} data-testid="import-excel-btn" className="border-navy/20 text-navy">
              <FileSpreadsheet className="w-4 h-4 mr-1" /> Import Excel
            </Button>
            <div className="relative flex-1 min-w-[220px] max-w-sm ml-auto">
              <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} placeholder="Cerca per cognome, nome o email…" className="pl-9" data-testid="admin-member-search" />
            </div>
          </div>
          <p className="text-sm text-muted-foreground" data-testid="admin-member-count">
            {_mq ? `${filteredMembers.length} di ${members.length} soci` : `${members.length} soci`}
          </p>

          <div className="bg-white rounded-lg border border-navy/10 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Carica</TableHead>
                  <TableHead>Club</TableHead>
                  <TableHead>Divisione</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMembers.map((m) => (
                  <TableRow key={m.id} data-testid={`admin-member-row-${m.id}`}>
                    <TableCell className="font-medium text-navy-deep">
                      <span className="inline-flex items-center gap-1.5">{m.nome} {m.cognome}
                        {m.onorario && <Laurel className="w-4 h-4 text-gold shrink-0" title="Socio Onorario" />}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{m.carica}</TableCell>
                    <TableCell className="text-sm">{m.club}</TableCell>
                    <TableCell className="text-sm">{m.divisione}</TableCell>
                    <TableCell className="text-sm">{m.email}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <input type="file" accept="image/*" hidden ref={(el) => (photoRefs.current[m.id] = el)}
                        onChange={(e) => uploadPhoto(m.id, e.target.files[0])} />
                      <Button variant="ghost" size="icon" onClick={() => photoRefs.current[m.id]?.click()} data-testid={`photo-btn-${m.id}`} title="Carica foto">
                        <ImageIcon className="w-4 h-4 text-navy" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setMemberDialog({ ...m })} data-testid={`edit-member-${m.id}`}>
                        <Pencil className="w-4 h-4 text-navy" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => genResetLink(m)} data-testid={`reset-link-${m.id}`} title="Genera link reset password">
                        <KeyRound className="w-4 h-4 text-gold" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => deleteMember(m.id)} data-testid={`delete-member-${m.id}`}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* EVENTI */}
        <TabsContent value="eventi" className="mt-6 space-y-4">
          <Button onClick={() => setEventDialog({ ...EMPTY_EVENT })} data-testid="add-event-btn" className="bg-navy hover:bg-navy-hover text-white">
            <Plus className="w-4 h-4 mr-1" /> Nuovo evento
          </Button>
          <div className="bg-white rounded-lg border border-navy/10 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Titolo</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Club</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((e) => (
                  <TableRow key={e.id} data-testid={`admin-event-row-${e.id}`}>
                    <TableCell className="font-medium text-navy-deep">{e.title}</TableCell>
                    <TableCell className="text-sm">{e.category}</TableCell>
                    <TableCell className="text-sm">{e.date}</TableCell>
                    <TableCell className="text-sm">{e.club}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" onClick={() => setEventDialog({ ...e })} data-testid={`edit-event-${e.id}`}>
                        <Pencil className="w-4 h-4 text-navy" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => deleteEvent(e.id)} data-testid={`delete-event-${e.id}`}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* STATISTICHE */}
        <TabsContent value="statistiche" className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[
            { title: "Soci per Club", data: breakdown.by_club, testid: "stats-by-club" },
            { title: "Soci per Divisione", data: breakdown.by_division, testid: "stats-by-division" },
          ].map((panel) => {
            const max = Math.max(1, ...panel.data.map((d) => d.count));
            return (
              <div key={panel.title} className="bg-white rounded-lg border border-navy/10 p-6" data-testid={panel.testid}>
                <div className="flex items-center gap-2 mb-5">
                  <BarChart2 className="w-5 h-5 text-gold" />
                  <h2 className="font-display text-xl font-semibold text-navy">{panel.title}</h2>
                  <span className="ml-auto text-xs uppercase tracking-wider font-bold text-muted-foreground">{breakdown.total} soci</span>
                </div>
                <div className="space-y-4">
                  {panel.data.map((d) => (
                    <div key={d.name}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-navy-deep font-medium truncate pr-2">{d.name}</span>
                        <span className="text-muted-foreground font-semibold tabular-nums">{d.count}</span>
                      </div>
                      <div className="h-2.5 rounded-full bg-navy/5 overflow-hidden">
                        <div className="h-full rounded-full bg-navy transition-all duration-500" style={{ width: `${(d.count / max) * 100}%` }} />
                      </div>
                    </div>
                  ))}
                  {panel.data.length === 0 && <p className="text-sm text-muted-foreground">Nessun dato disponibile.</p>}
                </div>
              </div>
            );
          })}
        </TabsContent>

        {/* CREDENZIALI */}
        <TabsContent value="credenziali" className="mt-6">
          <div className="bg-white rounded-lg border border-navy/10 p-8 max-w-2xl">
            <div className="w-12 h-12 rounded-md bg-gold/15 flex items-center justify-center mb-4">
              <KeyRound className="w-6 h-6 text-gold" />
            </div>
            <h2 className="font-display text-2xl font-bold text-navy">Generazione credenziali in blocco</h2>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              <strong>Genera credenziali mancanti</strong>: crea un account (password <strong>#kidsneedkiwanis</strong>) solo per i soci con email che non ne hanno ancora uno.
              <br /><strong>Imposta #kidsneedkiwanis a chi non l'ha cambiata</strong>: assegna la password universale ai soci che non hanno ancora scelto una password personale (i soci che l'hanno già cambiata <strong>non</strong> vengono toccati). Consigliato per l'avvio.
              <br /><strong>Rigenera TUTTE le password</strong>: assegna una nuova password provvisoria a <strong>tutti</strong> i soci con email, comprese quelle già personalizzate.
              <br />In tutti i casi le password vengono esportate in un <strong>file CSV</strong>; al primo accesso ogni socio dovrà cambiarla.
            </p>
            <div className="mt-4 rounded-md bg-navy/5 border border-navy/10 p-3 text-xs text-muted-foreground">
              🔒 Per motivi di sicurezza (anti-phishing) le password <strong>non</strong> vengono inviate via email né allegate.
              Una <strong>notifica senza password</strong> viene inviata a <strong>{NOTIFY_LABEL}</strong> per avvisare che il CSV è pronto nel pannello.
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button onClick={() => generateCredentials("new")} disabled={busy} data-testid="generate-credentials-btn" className="bg-gold hover:bg-gold-hover text-navy-deep font-semibold">
                <Upload className="w-4 h-4 mr-1" /> Genera credenziali mancanti & scarica CSV
              </Button>
              <Button onClick={() => generateCredentials("unchanged")} disabled={busy} data-testid="reset-unchanged-credentials-btn" className="bg-navy hover:bg-navy-hover text-white font-semibold">
                <KeyRound className="w-4 h-4 mr-1" /> Imposta #kidsneedkiwanis a chi non l'ha cambiata
              </Button>
              <Button onClick={() => generateCredentials("all")} disabled={busy} variant="outline" data-testid="regenerate-credentials-btn" className="border-navy/20 text-navy font-semibold">
                <RefreshCw className="w-4 h-4 mr-1" /> Rigenera TUTTE le password & scarica CSV
              </Button>
            </div>
          </div>
        </TabsContent>
        {/* PRIVACY */}
        <TabsContent value="privacy" className="mt-6 space-y-4">
          <div className="bg-white rounded-lg border border-navy/10 p-6" data-testid="privacy-version-card">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h2 className="font-display text-2xl font-bold text-navy">Versione informativa</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Versione attuale: <strong data-testid="privacy-version-value">v{privacyVersion?.version ?? "…"}</strong>
                  {privacyVersion?.updated_at && (
                    <> · aggiornata il {new Date(privacyVersion.updated_at).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" })}</>
                  )}
                </p>
                <p className="text-xs text-muted-foreground mt-2 max-w-xl leading-relaxed">
                  Dopo aver aggiornato il testo dell'informativa, pubblica una nuova versione: il popup ricomparirà a <strong>tutti</strong> i soci
                  (anche a chi aveva scelto "Non mostrare più") per richiedere una nuova accettazione.
                </p>
              </div>
              <Button onClick={publishPrivacyVersion} disabled={busy} className="bg-navy hover:bg-navy-hover text-white font-semibold" data-testid="privacy-publish-btn">
                <RefreshCw className="w-4 h-4 mr-1" /> Pubblica nuova versione
              </Button>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-navy/10 p-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h2 className="font-display text-2xl font-bold text-navy">Registro accettazioni Privacy</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Soci che hanno accettato l'informativa e le regole di utilizzo della Directory.
                  Totale: <strong data-testid="privacy-count">{privacyLog.length}</strong>
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button onClick={loadPrivacy} variant="outline" className="border-navy/20 text-navy font-semibold" data-testid="privacy-refresh-btn">
                  <RefreshCw className="w-4 h-4 mr-1" /> Aggiorna
                </Button>
                <Button onClick={exportPrivacyCsv} disabled={!privacyLog.length} className="bg-gold hover:bg-gold-hover text-navy-deep font-semibold" data-testid="privacy-export-btn">
                  <Download className="w-4 h-4 mr-1" /> Scarica CSV
                </Button>
              </div>
            </div>
            <div className="mt-5 border border-navy/10 rounded-md overflow-hidden">
              <Table data-testid="privacy-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Socio</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Data e ora accettazione</TableHead>
                    <TableHead className="text-center">Versione</TableHead>
                    <TableHead className="text-center">Non mostrare più</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {privacyLog.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Nessuna accettazione registrata.</TableCell></TableRow>
                  ) : privacyLog.map((p) => (
                    <TableRow key={p.id} data-testid={`privacy-row-${p.id}`}>
                      <TableCell className="font-semibold text-navy-deep">{p.name || "—"}</TableCell>
                      <TableCell className="text-muted-foreground">{p.email}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {p.privacy_accepted_at ? new Date(p.privacy_accepted_at).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                      </TableCell>
                      <TableCell className="text-center">v{p.privacy_version_accepted ?? 1}</TableCell>
                      <TableCell className="text-center">{p.privacy_hide ? "Sì" : "No"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>
        {/* RICHIESTE RESET */}
        <TabsContent value="reset" className="mt-6 space-y-4">
          <div className="bg-white rounded-lg border border-navy/10 p-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2">
                  <KeyRound className="w-6 h-6 text-gold" />
                  <h2 className="font-display text-2xl font-bold text-navy">Registro richieste reset password</h2>
                </div>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl leading-relaxed">
                  Ogni richiesta di reimpostazione password: l'email <strong>esattamente come digitata</strong> dal socio,
                  se corrisponde a un account e se l'invio della mail è andato a buon fine. Le righe in
                  <span className="text-destructive font-semibold"> rosso</span> indicano email che non corrispondono ad alcun
                  account (probabile errore di digitazione). Sono incluse anche le <strong>rigenerazioni</strong> fatte da un admin.
                  Totale: <strong data-testid="reset-count">{resetRequests.length}</strong>
                </p>
              </div>
              <Button onClick={loadResetRequests} variant="outline" className="border-navy/20 text-navy font-semibold" data-testid="reset-refresh-btn">
                <RefreshCw className="w-4 h-4 mr-1" /> Aggiorna
              </Button>
            </div>
            <div className="mt-5 border border-navy/10 rounded-md overflow-hidden">
              <Table data-testid="reset-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Data e ora</TableHead>
                    <TableHead>Email digitata</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-center">Account trovato</TableHead>
                    <TableHead className="text-center">Esito</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {resetRequests.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Nessuna richiesta registrata.</TableCell></TableRow>
                  ) : resetRequests.map((r) => {
                    const typo = !r.account_found;
                    return (
                    <TableRow key={r.id} data-testid={`reset-row-${r.id}`} className={typo ? "bg-red-50" : ""}>
                      <TableCell className="text-muted-foreground whitespace-nowrap">
                        {r.created_at ? new Date(r.created_at).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                      </TableCell>
                      <TableCell className={`font-semibold ${typo ? "text-destructive" : "text-navy-deep"}`}>{r.email}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {r.type === "admin_link"
                          ? <span title={r.requested_by ? `da ${r.requested_by}` : ""}>Rigenerazione admin</span>
                          : "Richiesta socio"}
                      </TableCell>
                      <TableCell className="text-center">
                        {r.account_found
                          ? <span className="text-green-700 font-semibold">Sì</span>
                          : <span className="text-destructive font-semibold">No — possibile errore email</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        {r.type === "admin_link"
                          ? <span className="text-navy font-semibold">{r.account_created ? "Link + account creato" : "Link generato"}</span>
                          : (!r.account_found
                              ? <span className="text-muted-foreground">Nessun invio</span>
                              : (r.email_sent
                                  ? <span className="text-green-700 font-semibold">Email inviata</span>
                                  : <div>
                                      <span className="text-destructive font-semibold">Invio fallito</span>
                                      <div className="text-[11px] text-muted-foreground mt-0.5">
                                        {(r.error_reason || "").includes("422")
                                          ? "Destinatario non recapitabile (422) — casella inesistente, piena o dominio che rifiuta"
                                          : (r.error_reason || "Errore del provider email")}
                                      </div>
                                    </div>))}
                      </TableCell>
                    </TableRow>
                  );})}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>
        {/* ORGANIGRAMMA */}
        <TabsContent value="organigramma" className="mt-6 space-y-6">
          <div className="bg-white rounded-lg border border-navy/10 p-6" data-testid="org-officers">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-gold" />
                <h2 className="font-display text-xl font-semibold text-navy">Cariche Distrettuali</h2>
              </div>
              <Button variant="outline" onClick={addOfficer} data-testid="add-officer-btn" className="border-navy/20 text-navy">
                <Plus className="w-4 h-4 mr-1" /> Aggiungi carica
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Definisci le cariche del Distretto (es. Governatore, Governatore Eletto, Segretario, Tesoriere) associando il socio corrispondente.
            </p>
            <div className="mt-5 space-y-3">
              {orgConfig.officers.length === 0 && <p className="text-sm text-muted-foreground italic">Nessuna carica configurata.</p>}
              {orgConfig.officers.map((o, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1.4fr_auto] gap-3 items-center" data-testid={`officer-row-${i}`}>
                  <Input placeholder="Carica (es. Governatore)" value={o.role} onChange={(e) => updOfficer(i, "role", e.target.value)} data-testid={`officer-role-${i}`} />
                  <MemberCombobox members={members} value={o.member_id} onChange={(v) => updOfficer(i, "member_id", v)} testid={`officer-member-${i}`} />
                  <Button variant="ghost" size="icon" onClick={() => rmOfficer(i)} data-testid={`officer-remove-${i}`}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-navy/10 p-6" data-testid="org-lieutenants">
            <div className="flex items-center gap-2">
              <Network className="w-5 h-5 text-gold" />
              <h2 className="font-display text-xl font-semibold text-navy">Luogotenenti Governatori</h2>
            </div>
            <p className="text-sm text-muted-foreground mt-2">Assegna il Luogotenente Governatore per ciascuna divisione.</p>
            <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
              {divisions.map((d) => (
                <div key={d} className="space-y-1.5">
                  <Label className="text-navy-deep">{d}</Label>
                  <MemberCombobox members={members} value={orgConfig.lieutenants[d] || ""} onChange={(v) => setLieut(d, v)} testid={`lieutenant-${d}`} />
                </div>
              ))}
            </div>
          </div>

          <Button onClick={saveOrg} disabled={busy} data-testid="save-org-btn" className="bg-navy hover:bg-navy-hover text-white font-semibold">
            <CheckCircle2 className="w-4 h-4 mr-1" /> Salva organigramma
          </Button>
        </TabsContent>

        {/* IMPOSTAZIONI */}
        <TabsContent value="impostazioni" className="mt-6">
          <div className="bg-white rounded-lg border border-navy/10 p-8 max-w-3xl" data-testid="settings-panel">
            <div className="flex items-center gap-2 mb-1">
              <Settings2 className="w-5 h-5 text-gold" />
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-gold">Aggiornamento soci</p>
            </div>
            <h2 className="font-display text-2xl font-bold text-navy">Aggiornamento dell'elenco tramite Excel</h2>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              L'elenco dei soci si aggiorna esclusivamente caricando manualmente il file Excel ufficiale del Distretto
              (colonne Kiwanis standard). I soci esistenti sono riconosciuti tramite il "Numero di membro" e aggiornati,
              i nuovi vengono aggiunti e quelli non più presenti nel file vengono rimossi. I soci deceduti indicati nel
              file vengono esclusi. Non è previsto alcun aggiornamento automatico.
            </p>

            <div className="rounded-md border border-navy/10 p-4 mt-6">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-muted-foreground">
                <CheckCircle2 className="w-4 h-4" /> Ultima importazione
              </div>
              <p className="text-navy-deep font-semibold mt-2">
                {syncStatus?.last_sync ? new Date(syncStatus.last_sync).toLocaleString("it-IT") : "Mai eseguita"}
              </p>
              {syncStatus?.last_result && !syncStatus.last_result.error && (
                <p className="text-xs text-muted-foreground mt-1">
                  {syncStatus.last_result.inserted} nuovi · {syncStatus.last_result.updated} aggiornati{syncStatus.last_result.removed ? ` · ${syncStatus.last_result.removed} rimossi` : ""} · {syncStatus.total_members} totali
                </p>
              )}
              {syncStatus?.last_result?.error && (
                <p className="text-xs text-destructive mt-1">Errore: {syncStatus.last_result.error}</p>
              )}
            </div>

            <input ref={settingsExcelRef} type="file" accept=".xlsx,.xls" hidden onChange={(e) => importExcel(e.target.files[0])} data-testid="settings-excel-input" />
            <Button onClick={() => settingsExcelRef.current?.click()} disabled={busy} data-testid="settings-import-excel-btn"
              className="mt-6 bg-navy hover:bg-navy-hover text-white font-semibold">
              <FileSpreadsheet className="w-4 h-4 mr-1" />
              {busy ? "Importazione in corso…" : "Importa da file Excel"}
            </Button>
          </div>

          <div className="bg-white rounded-lg border border-navy/10 p-8 max-w-3xl mt-6" data-testid="pdf-cover-panel">
            <div className="flex items-center gap-2 mb-1">
              <BookOpen className="w-5 h-5 text-gold" />
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-gold">Directory PDF</p>
            </div>
            <h2 className="font-display text-2xl font-bold text-navy">Copertina personalizzata</h2>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              Personalizza la copertina del PDF della Directory: imposta l'anno sociale e, facoltativamente, carica un'immagine
              di copertina (verrà usata a tutta pagina con il logo e i titoli in sovrimpressione).
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
              <div className="space-y-1.5">
                <Label>Anno sociale</Label>
                <Input placeholder="es. 2025-2026" value={pdfCover.social_year}
                  onChange={(e) => setPdfCover({ ...pdfCover, social_year: e.target.value })}
                  data-testid="pdf-cover-year" />
              </div>
              <div className="space-y-1.5">
                <Label>Immagine di copertina (facoltativa)</Label>
                <div className="flex items-center gap-3">
                  <input ref={coverImgRef} type="file" accept="image/*" hidden onChange={(e) => uploadCoverImg(e.target.files[0])} />
                  <Button type="button" variant="outline" onClick={() => coverImgRef.current?.click()} className="border-navy/20 text-navy" data-testid="pdf-cover-upload-btn">
                    <ImageIcon className="w-4 h-4 mr-1" /> Carica immagine
                  </Button>
                  {pdfCover.cover_image_url && (
                    <Button type="button" variant="ghost" onClick={() => setPdfCover({ ...pdfCover, cover_image_url: "" })} className="text-destructive" data-testid="pdf-cover-remove-btn">
                      <Trash2 className="w-4 h-4 mr-1" /> Rimuovi
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {pdfCover.cover_image_url && (
              <img src={authImageUrl(pdfCover.cover_image_url)} alt="Anteprima copertina"
                className="mt-4 h-40 rounded-md object-contain bg-navy/5 border border-navy/10" data-testid="pdf-cover-preview" />
            )}

            <Button onClick={savePdfCover} disabled={busy} className="mt-6 bg-navy hover:bg-navy-hover text-white font-semibold" data-testid="pdf-cover-save-btn">
              Salva impostazioni copertina
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      {/* Member dialog */}
      <Dialog open={!!memberDialog} onOpenChange={(o) => !o && setMemberDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="member-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">{memberDialog?.id ? "Modifica socio" : "Nuovo socio"}</DialogTitle></DialogHeader>
          {memberDialog && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                ["nome", "Nome"], ["cognome", "Cognome"], ["member_number", "Numero di membro"], ["carica", "Carica / Ruolo"],
                ["club", "Club"], ["divisione", "Divisione"], ["email", "Email"], ["telefono", "Telefono"],
                ["join_date", "Socio Kiwanis dal"],
              ].map(([key, label]) => (
                <div key={key} className="space-y-1.5">
                  <Label>{label}</Label>
                  <Input value={memberDialog[key] || ""} onChange={(e) => setMemberDialog({ ...memberDialog, [key]: e.target.value })} data-testid={`member-field-${key}`} />
                </div>
              ))}
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Indirizzo</Label>
                <Input value={memberDialog.indirizzo || ""} onChange={(e) => setMemberDialog({ ...memberDialog, indirizzo: e.target.value })} data-testid="member-field-indirizzo" />
              </div>
              <div className="flex items-center gap-2 sm:col-span-2 rounded-md border border-gold/40 bg-gold/5 px-3 py-2.5">
                <input type="checkbox" id="onorario-check" checked={!!memberDialog.onorario}
                  onChange={(e) => setMemberDialog({ ...memberDialog, onorario: e.target.checked })}
                  className="w-4 h-4 accent-[#B49759]" data-testid="member-field-onorario" />
                <Label htmlFor="onorario-check" className="cursor-pointer">Socio Onorario <span className="text-muted-foreground font-normal">(escluso dal totale soci)</span></Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMemberDialog(null)}>Annulla</Button>
            <Button onClick={saveMember} disabled={busy || !memberDialog?.cognome} className="bg-navy hover:bg-navy-hover text-white" data-testid="save-member-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event dialog */}
      <Dialog open={!!eventDialog} onOpenChange={(o) => !o && setEventDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="event-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">{eventDialog?.id ? "Modifica evento" : "Nuovo evento"}</DialogTitle></DialogHeader>
          {eventDialog && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Titolo</Label>
                <Input value={eventDialog.title} onChange={(e) => setEventDialog({ ...eventDialog, title: e.target.value })} data-testid="event-field-title" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Categoria</Label>
                  <Select value={eventDialog.category} onValueChange={(v) => setEventDialog({ ...eventDialog, category: v })}>
                    <SelectTrigger data-testid="event-field-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Distretto">Distretto</SelectItem>
                      <SelectItem value="Club">Club</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Data inizio</Label>
                  <Input type="date" value={eventDialog.date} onChange={(e) => setEventDialog({ ...eventDialog, date: e.target.value })} data-testid="event-field-date" />
                </div>
                <div className="space-y-1.5">
                  <Label>Data fine (facoltativa)</Label>
                  <Input type="date" value={eventDialog.end_date || ""} min={eventDialog.date || undefined} onChange={(e) => setEventDialog({ ...eventDialog, end_date: e.target.value })} data-testid="event-field-end-date" />
                </div>
                <div className="space-y-1.5">
                  <Label>Club</Label>
                  <Input value={eventDialog.club} onChange={(e) => setEventDialog({ ...eventDialog, club: e.target.value })} data-testid="event-field-club" />
                </div>
                <div className="space-y-1.5">
                  <Label>Divisione</Label>
                  <Input value={eventDialog.divisione} onChange={(e) => setEventDialog({ ...eventDialog, divisione: e.target.value })} data-testid="event-field-divisione" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Descrizione</Label>
                <Textarea rows={3} value={eventDialog.description} onChange={(e) => setEventDialog({ ...eventDialog, description: e.target.value })} data-testid="event-field-description" />
              </div>
              <div className="space-y-1.5">
                <Label>Link alla news (facoltativo)</Label>
                <Input placeholder="https://…" value={eventDialog.link || ""} onChange={(e) => setEventDialog({ ...eventDialog, link: e.target.value })} data-testid="event-field-link" />
              </div>
              <div className="space-y-1.5">
                <Label>Media (immagine oppure video)</Label>
                <div className="flex flex-wrap items-center gap-3">
                  <input ref={eventImgRef} type="file" accept="image/*" hidden onChange={(e) => uploadEventImg(e.target.files[0])} />
                  <Button type="button" variant="outline" onClick={() => eventImgRef.current?.click()} className="border-navy/20 text-navy" data-testid="event-upload-image-btn">
                    <ImageIcon className="w-4 h-4 mr-1" /> Carica immagine
                  </Button>
                  <input ref={eventVideoRef} type="file" accept="video/mp4,video/quicktime,video/webm,video/x-m4v" hidden onChange={(e) => uploadEventVideo(e.target.files[0])} />
                  <Button type="button" variant="outline" onClick={() => eventVideoRef.current?.click()} disabled={videoProgress !== null} className="border-navy/20 text-navy" data-testid="event-upload-video-btn">
                    <Video className="w-4 h-4 mr-1" /> {videoProgress !== null ? `Caricamento… ${videoProgress}%` : "Carica video (mp4)"}
                  </Button>
                </div>
                <Input placeholder="oppure incolla un URL immagine" value={eventDialog.image_url} onChange={(e) => setEventDialog({ ...eventDialog, image_url: e.target.value, video_url: e.target.value ? "" : eventDialog.video_url })} data-testid="event-field-image" />
                {videoProgress !== null && (
                  <div className="h-2 rounded bg-navy/10 overflow-hidden"><div className="h-full bg-gold transition-all" style={{ width: `${videoProgress}%` }} /></div>
                )}
                {eventDialog.video_url ? (
                  <div className="mt-2 space-y-1">
                    <video src={eventDialog.video_url} controls className="h-40 rounded-md bg-black/5 border border-navy/10" data-testid="event-video-preview" />
                    <button type="button" onClick={() => setEventDialog({ ...eventDialog, video_url: "" })} className="block text-xs text-destructive font-semibold" data-testid="event-remove-video-btn">Rimuovi video</button>
                  </div>
                ) : eventDialog.image_url ? (
                  <img src={eventDialog.image_url} alt="preview" className="mt-2 h-28 rounded-md object-contain bg-navy/5 border border-navy/10" />
                ) : null}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEventDialog(null)}>Annulla</Button>
            <Button onClick={saveEvent} disabled={busy || !eventDialog?.title} className="bg-navy hover:bg-navy-hover text-white" data-testid="save-event-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
